class CustomProductCard extends HTMLElement {
  constructor() {
    super();
    this.colorChips = Array.from(this.querySelectorAll('.js-color-chip'));
    this.variantImages = this.querySelectorAll('.js-variant-image');
    this.productLinks = this.querySelectorAll('.js-product-link');
    this.productUrl = this.getAttribute('data-product-url');
    this.quickAddButton = this.querySelector('.js-quick-add-button');
    this.hoverImage = this.querySelector('.hover-image');
    this.mediaWrapper = this.querySelector('.custom-card__media-wrapper');
    this.priceContainer = this.querySelector('.js-price-container');
    this.variantPrices = Array.from(this.querySelectorAll('.js-variant-price'));
    
    this.productHandle = this.getAttribute('data-product-handle');

    if (this.productHandle) {
      this.initializeCard();
    }
  }

  initializeCard() {
    this.setInitialActiveVariant();
    this.setupEventListeners();
  }

  setInitialActiveVariant() {
    const firstColorChip = this.colorChips[0];
    if (firstColorChip) {
      this.setActiveVariant(firstColorChip.dataset.variantId);
    }
  }

  setupEventListeners() {
    this.colorChips.forEach(chip => {
      chip.addEventListener('click', this.handleColorChipClick.bind(this));
    });

    if (this.quickAddButton) {
      this.quickAddButton.addEventListener('click', this.handleQuickAddClick.bind(this));
      this.quickAddButton.addEventListener('mouseenter', this.handleQuickAddMouseEnter.bind(this));
      this.quickAddButton.addEventListener('mouseleave', this.handleQuickAddMouseLeave.bind(this));
    }

    if (this.mediaWrapper) {
      this.mediaWrapper.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
      this.mediaWrapper.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    }
  }

  handleColorChipClick(event) {
    event.preventDefault();
    const clickedChip = event.target;
    const variantId = clickedChip.dataset.variantId;
    this.setActiveVariant(variantId);
  }

  setActiveVariant(variantId) {
    this.colorChips.forEach(chip => {
      chip.classList.toggle('active', chip.dataset.variantId === variantId);
    });

    this.updateProductImage(variantId);
    this.updateProductLinks(variantId);
    this.updateProductPrice(variantId);
  }

  updateProductImage(variantId) {
    this.variantImages.forEach(img => {
      img.classList.toggle('active', img.dataset.variantId === variantId);
    });
  }

  updateProductLinks(variantId) {
    if (!this.productUrl) {
      return;
    }
    const variantUrl = `${this.productUrl}?variant=${variantId}`;
    this.productLinks.forEach(link => {
      link.href = variantUrl;
    });
  }

  updateProductPrice(variantId) {
    if (!this.priceContainer || this.variantPrices.length === 0) {
      return;
    }
    const priceElement = this.variantPrices.find(price => price.dataset.variantId === variantId);
    if (priceElement) {
      this.priceContainer.innerHTML = priceElement.innerHTML;
    }
  }

  handleMouseEnter() {
    if (this.hoverImage && !this.isQuickAddHovered) {
      this.showHoverImage();
    }
  }

  handleMouseLeave() {
    if (this.hoverImage && !this.isQuickAddHovered) {
      this.showActiveVariantImage();
    }
  }

  handleQuickAddMouseEnter() {
    this.isQuickAddHovered = true;
    this.showActiveVariantImage();
  }

  handleQuickAddMouseLeave() {
    this.isQuickAddHovered = false;
    if (this.mediaWrapper.matches(':hover')) {
      this.showHoverImage();
    }
  }

  showHoverImage() {
    this.variantImages.forEach(img => {
      img.style.opacity = '0';
    });
    if (this.hoverImage) {
      this.hoverImage.style.opacity = '1';
    }
  }

  showActiveVariantImage() {
    if (this.hoverImage) {
      this.hoverImage.style.opacity = '0';
    }
    const activeVariantImage = this.querySelector('.js-variant-image.active');
    if (activeVariantImage) {
      activeVariantImage.style.opacity = '1';
    }
  }

  handleQuickAddClick(event) {
    event.preventDefault();
    
    const activeChip = this.querySelector('.js-color-chip.active');
    if (!activeChip) {
      return;
    }
    
    const variantId = activeChip.dataset.variantId;
  
    fetch(`/products/${this.productHandle}?view=product-data`)
      .then(response => response.text())
      .then(text => {
        let data;
        try {
          const jsonText = text.trim();
          data = JSON.parse(jsonText);
        } catch (error) {
          throw new Error('Invalid JSON response');
        }
  
        const variant = data.variants.find(v => v.id.toString() === variantId);
      
        if (!variant) {
          throw new Error('Variant not found');
        }
  
        let variantImage = variant.featured_media ? variant.featured_media.preview_image.src : null;
        if (!variantImage && data.product.image) {
          variantImage = data.product.image.src;
        }
  
        const availableVariants = data.variants
          .filter(v => v.available && v.featured_media)
          .map(v => ({
            id: v.id.toString(),
            color: v.title,
            isActive: v.id.toString() === variantId
          }));
  
        const quickAddData = {
          productHandle: data.product.handle,
          productTitle: data.product.title,
          productFeaturedImage: data.product.image ? data.product.image.src : null,
          variantTitle: variant.title,
          price: variant.price,
          image: variantImage,
          variantId: variant.id,
          availableQuantity: variant.inventory_quantity,
          inventoryManagement: variant.inventory_management,
          inventoryPolicy: variant.inventory_policy,
          productData: data.product,
          variantsData: data.variants.map(v => ({
            ...v,
            featured_media: v.featured_media ? {
              ...v.featured_media,
              preview_image: {
                ...v.featured_media.preview_image,
                src: v.featured_media.preview_image.src
              }
            } : null
          })),
          availableVariants: availableVariants
        };
  
        const quickAddEvent = new CustomEvent('quickAddReady', { 
          detail: quickAddData
        });
        document.dispatchEvent(quickAddEvent);
      
        if (typeof window.showQuickAddCursor === 'function') {
          window.showQuickAddCursor(event);
        }
      })
      .catch(error => {
        console.error('Error in handleQuickAddClick:', error);
      });
  }

  getOptimizedImageUrl(imageUrl) {
    if (!imageUrl) {
      return '';
    }
    const baseUrl = imageUrl.split('?')[0];
    return `${baseUrl}?width=302&height=436&crop=center`;
  }
}

customElements.define('custom-product-card', CustomProductCard);